import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { deleteLongAbsence, deleteShortAbsence } from '~/data/absences';
import { useEmployeeAbsenceData } from '~/data/absences/useEmployeeAbsenceData';
import { AbsencesTabContent_OnUpdateLegacy } from '~/library/AbsencesTabContent/component';

export type DeleteAbsenceDialogProps = {
  employeeId: number;
  absenceId: string | undefined;
  onClose: () => void;
  onDeleteAbsence?: () => void;
  open: boolean;
} & AbsencesTabContent_OnUpdateLegacy;

export const DeleteAbsenceDialog = (props: DeleteAbsenceDialogProps) => {
  const {
    employeeId,
    absenceId,
    onDeleteAbsence,
    onClose,
    open,
    onUpdateLegacy,
  } = props;

  const { t } = useTranslation();

  const { absenceEntries, mutate } = useEmployeeAbsenceData({ employeeId });

  const handleDeleteAbsence = (absenceId: string | undefined) => {
    mutate((data) => {
      if (data) {
        const updatedLongs = data.absenceLongs.filter(
          (entry) => entry.fAbsenceLongUId !== absenceId,
        );
        const updatedShorts = data.absenceShorts.filter(
          (entry) => entry.fAbsenceShortUId !== absenceId,
        );
        return {
          ...data,
          absenceLongs: updatedLongs,
          absenceShorts: updatedShorts,
        };
      }
      return data;
    }, false);
  };

  const handleClose = () => {
    onClose();
  };

  const entry = absenceEntries.find((entry) => entry.uid === absenceId);

  const handleDelete = async () => {
    if (entry) {
      try {
        if (entry.type === 'SHORT') {
          await deleteShortAbsence(employeeId, entry.uid);
        } else {
          await deleteLongAbsence(employeeId, entry.uid);
        }
        handleDeleteAbsence(absenceId);
        onDeleteAbsence && onDeleteAbsence();
        if (onUpdateLegacy) {
          onUpdateLegacy(['absence-delete']);
        }
        onClose();
      } catch (error) {
        console.error('Error deleting absence entry:', error);
      }
    }
  };

  return (
    <Dialog
      open={open}
      aria-labelledby="delete-absence-confirmation"
      maxWidth="sm"
    >
      <DialogTitle id="delete-absence-confirmation">
        {t('laConfirmDelete') + '?'}
      </DialogTitle>
      <DialogContent>
        {entry && entry.reason
          ? entry.reason.name +
            ' - ' +
            moment(entry.startDate).format('L') +
            ' - ' +
            moment(entry.endDate).format('L')
          : ''}
      </DialogContent>
      <DialogActions sx={{ margin: '8px 20px' }}>
        <Button
          variant="text"
          component="a"
          onClick={handleClose}
          color="primary"
        >
          {t('laNo')}
        </Button>
        <Button variant="contained" color="primary" onClick={handleDelete}>
          {t('laYes')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
